import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import  { ExportLatestQuestionaireDataJsonToCsv }  from 'Modules/questionnaires/tools/tools';
import LoadingBox from '@pzh/crm/src/Modules/loading/components/LoadingBox';
import { useQuestionnairesByCustomerContactId } from '@pzh/crm/src/Modules/questionnaires/questionnairesSlice';
import { useContactsByCustomerId } from 'Modules/contacts/contactsSlice';
import { useApi } from 'Modules/api/components/ApiProvider';

export const CustomerLatestQuestionaireCSVAttachment = ({ customerId, name, value, onChange }) => {
    const { cloud } = useApi();

    const handleChange = useMemo(
        () => (value) => {
            const newValue = value;
            onChange(name, newValue);
        },
        [onChange, name]
    );

    const [exportComplete, setExportComplete] = useState(false);
    const [exportError, setExportError] = useState(false);

    const [contacts] = useContactsByCustomerId(customerId);

    const patients = contacts.filter((c) => c.isPatient);
    const primaryContact = contacts.filter((c) => c.isPrimaryContact);

    const uploadPaths = [];
    const csvDataArray = []; // Array to store CSV data and metadata

    const [
        customer_questionnaires,
        { loading: questionnaireLoading, initialized: questionnaireInitialized, error: questionnaireError },
    ] = useQuestionnairesByCustomerContactId(customerId);

    const error = questionnaireError;
    const loading = questionnaireLoading;
    const initialized = questionnaireInitialized;

    const hasgenerateAndUploadCSVsRun = useRef(false); // Track if hasgenerateAndUploadCSVsRun has already run

    useEffect(() => {
        const generateAndUploadCSVs = async () => {
            if (!loading && !error && customer_questionnaires && customer_questionnaires.length > 0) {

                if (hasgenerateAndUploadCSVsRun.current)
                    return; // Prevent re-running if already executed
                hasgenerateAndUploadCSVsRun.current = true; // Mark as executed


                // Step 1: Generate all CSVs
                for (const patient of patients) {
                    const patient_questionnaires = customer_questionnaires
                        .filter((q) => q.contactId === patient.id)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    if (!patient_questionnaires || patient_questionnaires.length === 0) continue;

                    const questionnairesSorted = patient_questionnaires.sort((a, b) => a.id - b.id)
                    const [questionnaire] = questionnairesSorted.slice(-1);
                    if (!questionnaire) continue;

                    const csv_data = ExportLatestQuestionaireDataJsonToCsv(patient, questionnaire, primaryContact[0]);
                    const upload_data = {
                        path: `/PzH/Kunden/${customerId}/`,
                        file: `questionaire_${customerId}_${patient.id}.csv`,
                    };
                    const upload_full_path = `${upload_data.path}${upload_data.file}`;
                    uploadPaths.push(upload_full_path);

                    csvDataArray.push({
                        csv_data,
                        upload_full_path,
                        patientId: patient.id,
                    });
                }

                // Step 2: Upload all CSVs
                try {
                    for (const { csv_data, upload_full_path, patientId } of csvDataArray) {
                        const csv_blob_data = new Blob([csv_data], { type: 'text/csv' });
                        const temporaryFile = new File([csv_blob_data], `tempfile_${patientId}.csv`, { type: 'text/csv' });

                        await cloud.upload(upload_full_path, temporaryFile);
                    }

                    // Mark as complete only after all uploads are done
                    setExportComplete(true);
                    handleChange(uploadPaths);
                } catch (upload_error) {
                    console.error("ERROR: Uploading CSV files:", upload_error);
                    setExportError(true);
                }
            }
        };

        generateAndUploadCSVs();
    }, [loading, error, customer_questionnaires, patients, primaryContact, cloud, customerId, handleChange, csvDataArray, uploadPaths]);

    return (
        <LoadingBox loading={!exportComplete} initialized={initialized}>
            <p>
                Erhebungsbogen (CSV)
                {!exportError && !exportComplete && ' - Wird geladen...'}
                {!exportError && exportComplete && ' - Als Anhang bereit'}
                {exportError && ' - [Fehler beim Laden des Erhebungsbogen!]'}
            </p>
        </LoadingBox>
    );
};

CustomerLatestQuestionaireCSVAttachment.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

CustomerLatestQuestionaireCSVAttachment.defaultProps = {};

export default CustomerLatestQuestionaireCSVAttachment;
